/** @format */

import LoadingSpinner from '@/components/ui/client/LoadingSpinner';
import { ScrollView, ScrollViewRef } from 'devextreme-react/scroll-view';
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

interface InfiniteScrollListProps {
  dataLength: number;
  id: string;
  hasMore: boolean;
  fetchNext(): Promise<unknown> | void;
  children: ReactNode;
}

const StyledInfiniteScrollList = styled.div`
  height: 250px;
  width: 100%;
`;

function InfiniteScrollList(props: InfiniteScrollListProps) {
  const scrollbarId = `${props.id}scrollBar`;
  const isLoadingRef = useRef(false);
  const previousDataLength = useRef(props.dataLength);
  const scrollRef = useRef<ScrollViewRef>(null);
  const [isLoading, setIsLoading] = useState(false);

  const triggerNext = useCallback(() => {
    if (!props.hasMore || isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);
    Promise.resolve().then(() => props.fetchNext());
  }, [props.fetchNext, props.hasMore]);

  useEffect(() => {
    const didReceiveMoreRows = props.dataLength > previousDataLength.current;
    const noMoreData = !props.hasMore;

    if ((didReceiveMoreRows || noMoreData) && isLoadingRef.current) {
      isLoadingRef.current = false;
      setIsLoading(false);
    }

    previousDataLength.current = props.dataLength;
  }, [props.hasMore, props.dataLength]);

  const handleScroll = useCallback(
    (e: { reachedBottom?: boolean }) => {
      if (e?.reachedBottom) {
        triggerNext();
      }
    },
    [triggerNext],
  );

  return (
    <StyledInfiniteScrollList>
      <ScrollView id={scrollbarId} ref={scrollRef} direction={'vertical'} height={'100%'} onScroll={handleScroll}>
        <div style={{ minHeight: '100%' }}>
          {!props.dataLength && !props.hasMore && <FormattedMessage defaultMessage={'No Results Found'} description={'Notification that a search did not find any results'} />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingBlock: '4px' }}>{props.children}</div>
          {isLoading && props.hasMore && (
            <div
              style={{
                height: '20px',
                marginBlock: '4px',
              }}
            >
              <LoadingSpinner />
            </div>
          )}
        </div>
      </ScrollView>
    </StyledInfiniteScrollList>
  );
}

export default InfiniteScrollList;
